/**
 * React hook for PostHog feature flags
 */

import { useState, useEffect, useMemo } from 'react';
import { usePosthog } from './usePosthog';
import { featureFlagManager } from '~/services/posthog';

/**
 * Hook to check if a feature flag is enabled
 * Uses global feature flag manager to prevent memory leaks
 */
export const useFeatureFlag = (flagKey: string, defaultValue: boolean = false): boolean => {
  const posthogService = usePosthog();

  // Get initial flag value immediately using the faster direct service call
  const initialValue = useMemo(
    () => posthogService.isFeatureEnabled(flagKey, defaultValue),
    [posthogService, flagKey, defaultValue],
  );

  const [isEnabled, setIsEnabled] = useState<boolean>(initialValue);

  useEffect(() => {
    // Initialize the manager with PostHog service for subscriptions
    featureFlagManager.initialize(posthogService);

    // Get current flag value in case it changed since initial render
    const currentValue = featureFlagManager.getFeatureFlag(flagKey, defaultValue);
    setIsEnabled(currentValue);

    // Subscribe to flag updates
    const unsubscribe = featureFlagManager.subscribe(flagKey, (enabled) => {
      setIsEnabled(enabled);
    });

    // Cleanup subscription on unmount or dependency change
    return () => {
      unsubscribe();
    };
  }, [flagKey, defaultValue, posthogService]);

  return isEnabled;
};